import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  Auth0Provider as RealAuth0Provider,
  useAuth0 as useRealAuth0,
  User,
} from '@auth0/auth0-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, toast } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faLock,
  faRightFromBracket,
  faRightToBracket,
  faShieldHalved,
} from '@crisp/crisp-ui/icons/classic/regular';
import { extractRolesFromClaims, hasAuthoringAccess, isDocsAdmin } from './roles';
import { loginConnection, resolveSite } from './sites';
import styles from './AuthRequiredCard.module.css';

export interface UserAccessInfo {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  roles: string[];
  permissions: string[];
  hasAccess: boolean;
  requiresAuth: boolean;
  isDenied: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  isAdmin: boolean;
  setMockRoles?: (roles: string[]) => void;
  setMockAuthenticated?: (auth: boolean) => void;
  isMock: boolean;
}

interface MockAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  roles: string[];
  loginWithRedirect: () => Promise<void>;
  logout: (options?: any) => void;
  getAccessToken: () => Promise<string | null>;
  setRoles: (roles: string[]) => void;
  setAuthenticated: (auth: boolean) => void;
}

const MockAuthContext = createContext<MockAuthContextType | null>(null);

const DEFAULT_MOCK_USER: User = {
  name: 'Alex Rivera',
  email: 'alex@gocrisp.com',
  picture: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=128&fit=crop&q=80',
  sub: 'mock|crisp_user_1',
  'https://crisp.com/roles': ['authenticated', 'walmart', 'mission-control', 'target', 'ai-master-data', 'solys'],
  'https://gocrisp.com/roles': ['authenticated', 'walmart', 'mission-control', 'target', 'ai-master-data', 'solys'],
};

export const MockAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Check localStorage for saved mock auth state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const saved = localStorage.getItem('crisp_mock_auth');
    return saved !== null ? saved === 'true' : true; // Default to true in mock mode for easy dev exploration
  });

  const [roles, setRolesState] = useState<string[]>(() => {
    const saved = localStorage.getItem('crisp_mock_roles');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    return ['authenticated', 'walmart', 'mission-control', 'target', 'ai-master-data', 'solys'];
  });

  const setRoles = (newRoles: string[]) => {
    setRolesState(newRoles);
    localStorage.setItem('crisp_mock_roles', JSON.stringify(newRoles));
  };

  const setAuthenticated = (auth: boolean) => {
    setIsAuthenticated(auth);
    localStorage.setItem('crisp_mock_auth', String(auth));
  };

  const loginWithRedirect = async () => {
    setAuthenticated(true);
  };

  const logout = () => {
    setAuthenticated(false);
  };

  const getAccessToken = async (): Promise<string | null> => {
    return isAuthenticated ? 'mock-crisp-dev-jwt-token' : null;
  };

  const mockUser: User | null = isAuthenticated
    ? {
        ...DEFAULT_MOCK_USER,
        'https://crisp.com/roles': roles,
        'https://gocrisp.com/roles': roles,
      }
    : null;

  return (
    <MockAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading: false,
        user: mockUser,
        roles,
        loginWithRedirect,
        logout,
        getAccessToken,
        setRoles,
        setAuthenticated,
      }}
    >
      {children}
    </MockAuthContext.Provider>
  );
};

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

const isAuth0Configured = Boolean(domain && clientId);

interface Auth0PermissionState {
  permissions: string[];
  isLoading: boolean;
}

const Auth0PermissionContext = createContext<Auth0PermissionState>({
  permissions: [],
  isLoading: false,
});

function readAccessTokenPermissions(token: string): string[] {
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    const claimKeys = [
      'permissions',
      'https://gocrisp.com/permissions',
      'https://crisp.com/permissions',
    ];
    const permissions = claimKeys.flatMap((key) =>
      Array.isArray(payload[key])
        ? payload[key].filter((item): item is string => typeof item === 'string')
        : []
    );
    return [...new Set(permissions)];
  } catch {
    return [];
  }
}

const RealAuthPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useRealAuth0();
  const [state, setState] = useState<Auth0PermissionState>({
    permissions: [],
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;
    if (auth.isLoading) return () => {
      cancelled = true;
    };
    if (!auth.isAuthenticated) {
      setState({ permissions: [], isLoading: false });
      return () => {
        cancelled = true;
      };
    }

    setState((current) => ({ ...current, isLoading: true }));
    void auth.getAccessTokenSilently()
      .then((token) => {
        if (!cancelled) {
          setState({
            permissions: readAccessTokenPermissions(token),
            isLoading: false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ permissions: [], isLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [auth.isLoading, auth.isAuthenticated, auth.getAccessTokenSilently]);

  // A failed redirect callback (CORS, invalid state, IdP error) leaves the user
  // signed out with no other signal.
  useEffect(() => {
    if (!auth.error) return;
    console.error('Auth0 sign-in failed:', auth.error);
    toast.add({ type: 'error', title: 'Sign-in failed', description: auth.error.message });
  }, [auth.error]);

  return (
    <Auth0PermissionContext.Provider value={state}>
      {children}
    </Auth0PermissionContext.Provider>
  );
};

export const CrispAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (isAuth0Configured) {
    return (
      <RealAuth0Provider
        domain={domain!}
        clientId={clientId!}
        onRedirectCallback={(appState) => {
          const returnTo =
            typeof appState?.returnTo === 'string' ? appState.returnTo : window.location.pathname;
          window.history.replaceState({}, document.title, returnTo);
        }}
        authorizationParams={{
          redirect_uri: window.location.origin,
          ...(audience ? { audience } : {}),
        }}
      >
        <RealAuthPermissionsProvider>{children}</RealAuthPermissionsProvider>
      </RealAuth0Provider>
    );
  }

  return <MockAuthProvider>{children}</MockAuthProvider>;
};

export interface CrispAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  loginWithRedirect: () => Promise<void> | void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  roles: string[];
  permissions: string[];
  isAdmin: boolean;
  setMockRoles?: (roles: string[]) => void;
  setMockAuthenticated?: (auth: boolean) => void;
  isMock: boolean;
}

/**
 * Universal hook that works with both real Auth0 and Mock mode.
 */
export function useCrispAuth(): CrispAuthResult {
  const mockContext = useContext(MockAuthContext);
  const permissionState = useContext(Auth0PermissionContext);

  if (!isAuth0Configured && mockContext) {
    return {
      isAuthenticated: mockContext.isAuthenticated,
      isLoading: mockContext.isLoading,
      user: mockContext.user,
      loginWithRedirect: mockContext.loginWithRedirect,
      logout: mockContext.logout,
      getAccessToken: mockContext.getAccessToken,
      roles: mockContext.roles as string[],
      permissions: [],
      isAdmin: isDocsAdmin(mockContext.roles),
      setMockRoles: mockContext.setRoles,
      setMockAuthenticated: mockContext.setAuthenticated,
      isMock: true,
    };
  }

  try {
    const realAuth = useRealAuth0();
    const claimedRoles = extractRolesFromClaims(realAuth.user as Record<string, unknown> | undefined);
    const roles: string[] =
      claimedRoles.length > 0
        ? claimedRoles
        : realAuth.isAuthenticated
          ? ['authenticated']
          : [];

    const getAccessToken = async (): Promise<string | null> => {
      try {
        if (!realAuth.isAuthenticated) return null;
        return await realAuth.getAccessTokenSilently();
      } catch (err) {
        console.warn('Failed to acquire Auth0 access token:', err);
        return null;
      }
    };

    return {
      isAuthenticated: realAuth.isAuthenticated,
      isLoading: realAuth.isLoading || permissionState.isLoading,
      user: realAuth.user || null,
      loginWithRedirect: async () => {
        const site = resolveSite(window.location.pathname);
        const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const connection = await loginConnection(site);
        return realAuth.loginWithRedirect({
          appState: { returnTo },
          authorizationParams: connection ? { connection } : {},
        });
      },
      logout: () => {
        const site = resolveSite(window.location.pathname);
        return realAuth.logout({
          logoutParams: { returnTo: `${window.location.origin}${site.path || '/'}` },
        });
      },
      getAccessToken,
      roles,
      permissions: permissionState.permissions,
      isAdmin: hasAuthoringAccess(roles, permissionState.permissions),
      isMock: false,
    };
  } catch {
    // If used outside provider or in fallback
    return {
      isAuthenticated: false,
      isLoading: false,
      user: null,
      loginWithRedirect: async () => {},
      logout: () => {},
      getAccessToken: async () => null,
      roles: [] as string[],
      permissions: [] as string[],
      isAdmin: false,
      isMock: true,
    };
  }
}

/**
 * Helper hook to inspect user access permissions for a page or group.
 */
export function useUserAccess(pageAccess: string[] = ['public']): UserAccessInfo {
  const auth = useCrispAuth();
  const { isAuthenticated, isLoading, user, roles, permissions, loginWithRedirect, logout, getAccessToken, isMock, isAdmin } = auth;

  // If page requires only public access
  const isPublic = pageAccess.length === 0 || pageAccess.includes('public');
  if (isPublic) {
    return {
      isAuthenticated,
      isLoading,
      user,
      roles,
      permissions,
      hasAccess: true,
      requiresAuth: false,
      isDenied: false,
      login: loginWithRedirect,
      logout,
      getAccessToken,
      isAdmin,
      setMockRoles: auth.setMockRoles,
      setMockAuthenticated: auth.setMockAuthenticated,
      isMock,
    };
  }

  // If user is not authenticated at all
  if (!isAuthenticated) {
    return {
      isAuthenticated,
      isLoading,
      user,
      roles,
      permissions,
      hasAccess: false,
      requiresAuth: true,
      isDenied: false,
      login: loginWithRedirect,
      logout,
      getAccessToken,
      isAdmin,
      setMockRoles: auth.setMockRoles,
      setMockAuthenticated: auth.setMockAuthenticated,
      isMock,
    };
  }

  // User is authenticated. Check if role matches page allowed groups.
  // 'authenticated' group means any logged in user can view.
  const userRoles: string[] = roles || [];
  const hasMatchingRole =
    pageAccess.includes('authenticated') ||
    pageAccess.some((group) => userRoles.includes(group)) ||
    isAdmin ||
    isDocsAdmin(userRoles);

  return {
    isAuthenticated,
    isLoading,
    user,
    roles,
    permissions,
    hasAccess: hasMatchingRole,
    requiresAuth: false,
    isDenied: !hasMatchingRole,
    login: loginWithRedirect,
    logout,
    getAccessToken,
    isAdmin,
    setMockRoles: auth.setMockRoles,
    setMockAuthenticated: auth.setMockAuthenticated,
    isMock,
  };
}

/**
 * Card displayed when access to a page is restricted.
 */
export const AuthRequiredCard: React.FC<{
  access: string[];
  title?: string;
  /** The user is signed in without the claims this site's login needs. */
  loginDenied?: boolean;
}> = ({ access, title, loginDenied = false }) => {
  const { isAuthenticated, isLoading, isDenied, login, logout, user, setMockRoles, setMockAuthenticated, isMock } =
    useUserAccess(access);
  const siteName = resolveSite(window.location.pathname).name;
  const loginStarted = useRef(false);
  const loginRef = useRef(login);
  const [redirectFailed, setRedirectFailed] = useState(false);
  loginRef.current = login;

  // Unauthenticated visitors go straight to the IdP. The card is only for
  // signed-in users who still lack access, or when the redirect itself fails.
  useEffect(() => {
    if (loginStarted.current || isLoading || isAuthenticated || isDenied) return;
    loginStarted.current = true;
    void Promise.resolve(loginRef.current()).catch(() => {
      setRedirectFailed(true);
    });
  }, [isAuthenticated, isDenied, isLoading]);

  if (!isDenied && !redirectFailed) {
    return null;
  }

  if (loginDenied && isAuthenticated) {
    const account = user?.email || user?.name;
    return (
      <div className={styles.wrap}>
        <Card>
          <CardHeader>
            <FontAwesomeIcon icon={faShieldHalved} className={`icon-slot ${styles.icon}`} />
            <CardTitle>No access to {siteName}</CardTitle>
            <CardDescription>
              {account ? <>You're signed in as <strong>{account}</strong>, but this</> : 'This'} account can't
              open {siteName} pages that need sign-in. Sign in with another account, or ask your Crisp account
              administrator for access.
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.actions}>
            <Button variant="outline" onClick={() => logout()} iconPrimary={<FontAwesomeIcon icon={faRightFromBracket} />}>
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const readableGroups = access
    .filter((g) => g !== 'public')
    .map((g) => g.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(', ');

  return (
    <div className={styles.wrap}>
      <Card>
        <CardHeader>
          <FontAwesomeIcon
            icon={isDenied ? faShieldHalved : faLock}
            className={`icon-slot ${styles.icon}`}
          />
          <CardTitle>{isDenied ? 'Access Restricted' : 'Authentication Required'}</CardTitle>
          <CardDescription>
            {isDenied ? (
              <>
                This document ({title ? `"${title}"` : 'page'}) is restricted to users in the{' '}
                <strong>{readableGroups}</strong> group. Please reach out to your Crisp account
                administrator to request access.
              </>
            ) : (
              <>
                This documentation requires authentication to view. Sign in to access{' '}
                {title ? `"${title}"` : 'restricted content'}.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className={styles.actions}>
          {!isAuthenticated ? (
            <Button onClick={() => login()} iconPrimary={<FontAwesomeIcon icon={faRightToBracket} />}>
              Sign in to {siteName}
            </Button>
          ) : null}

          {isMock && (
            <Button
              variant="outline"
              onClick={() => {
                if (setMockAuthenticated) setMockAuthenticated(true);
                if (setMockRoles) setMockRoles(['authenticated', ...access]);
              }}
              iconPrimary={<FontAwesomeIcon icon={faCircleCheck} />}
            >
              [Dev] Grant Access
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
